import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import "./split-button.scss";
import { useResourcePermissions } from "../../hooks/resource-permissions";
import MODULES from "../../../constants/modules";

export interface OptionType {
  value: string;
  id: number;
}

interface SplitButtonProps {
  fieldName: string;
  createFieldName: string;
  value: string; // This expects a string, not an array
  options: OptionType[];
  optionsCreate: any[];
  onSelect?: (value: OptionType) => void;
  navigateTo: string;
  resourceName: string;
}

const SplitButtonComponent: React.FC<SplitButtonProps> = ({
  fieldName,
  value,
  options,
  onSelect,
  createFieldName,
  optionsCreate,
  resourceName,
}) => {
  const { canRead, canWrite, canDelete } = useResourcePermissions(
    resourceName
  );
  const { canImport } = useResourcePermissions(MODULES.IMPORT);
  const [open, setOpen] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    option: OptionType
  ) => {
    setOpen(false);
    if (onSelect) {
      onSelect(option);
    }
  };

  const handleToggle = () => {
    setOpenCreate(false);
    setOpen((prevOpen) => !prevOpen);
  };

  const handleCreateToggle = () => {
    setOpen(false);
    setOpenCreate((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };

  const handleCreateClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpenCreate(false);
  };

  return (
    <div className="split-button-container" ref={anchorRef}>
      <ButtonGroup
        variant="contained"
        className={`custom-button-group ${!canWrite ? "disable-button" : ""}`}
      >
        <Button
          aria-controls={openCreate ? `${createFieldName}-menu` : undefined}
          aria-expanded={openCreate ? "true" : undefined}
          aria-haspopup="menu"
          onClick={handleCreateToggle}
          className="custom-button"
          disabled={!canWrite}
        >
          {value}
        </Button>
        <Button
          size="small"
          className="custom-button"
          aria-controls={open ? `${fieldName}-menu` : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-haspopup="menu"
          onClick={handleToggle}
          disabled={!canWrite}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{ zIndex: 1 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id={`${fieldName}-menu`} autoFocusItem>
                  {options.map((option, index) => (
                    <MenuItem
                      key={index}
                      onClick={(event) => handleMenuItemClick(event, option)}
                      disabled= {(option.value === "Import Leads" || option.value === "Import Customers") && !canImport}
                    >
                      {option.value}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      <Popper
        sx={{ zIndex: 1, width: "168px" }}
        open={openCreate}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom-start"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "left bottom" : "left bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleCreateClose}>
                <MenuList id={`${createFieldName}-menu`} autoFocusItem>
                  {optionsCreate.map((option, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        setOpenCreate(false);
                        option?.onClick();
                      }}
                    >
                      <img src={option?.icon} alt={option?.icon} />
                      {option?.value}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

export default SplitButtonComponent;
